import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  admin,
  usd,
  type AdminAccount,
  type AdminAccountDetail,
  type AdminInvite,
  type AdminOverview,
} from '../lib/api';
import { useErrorText } from '../lib/errors';
import { useLoad } from '../lib/useLoad';
import { useT, type Translate } from '../lib/i18n';
import { useDateFormat } from '../lib/dates';
import { useSession } from '../lib/session';
import { Display } from '../components/Display';
import { useToast } from '../components/Toaster';
import { Skeleton } from '../components/Skeleton';
import { TrashIcon } from '../components/Icons';
import { Catalogue } from '../components/Catalogue';

type Tab = 'overview' | 'catalogue' | 'accounts' | 'invites';
const TABS: Tab[] = ['overview', 'catalogue', 'accounts', 'invites'];

/**
 * The operator's side of the studio.
 *
 * Reachable only by someone the server's own environment lists as staff — the
 * nav entry and this route are a convenience, not the guard; every request
 * behind them is checked again.
 */
export function AdminPage() {
  const t = useT();
  const { user } = useSession();
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab') as Tab | null;
  const tab: Tab = asked && TABS.includes(asked) ? asked : 'overview';

  const go = (next: Tab) => {
    params.set('tab', next);
    setParams(params, { replace: true });
  };

  // The guard that matters is on the server; this only decides what to draw.
  if (!user?.staff) return null;

  return (
    <div className="app__inner">
      <header className="pagehead">
        <Display text={t('admin.title')} />
        <div className="segmented" role="group" aria-label={t('admin.title')}>
          {TABS.map((id) => (
            <button
              key={id}
              type="button"
              className={tab === id ? 'is-on' : ''}
              aria-pressed={tab === id}
              onClick={() => go(id)}
            >
              {t(`admin.tab.${id}`)}
            </button>
          ))}
        </div>
      </header>

      {tab === 'overview' ? (
        <Overview />
      ) : tab === 'catalogue' ? (
        <Catalogue />
      ) : tab === 'accounts' ? (
        <Accounts />
      ) : (
        <Invites />
      )}
    </div>
  );
}

/* ------------------------------- dashboard ------------------------------ */

function Overview() {
  const t = useT();
  const { data } = useLoad(() => admin.overview(), { initial: undefined as AdminOverview | undefined });

  if (!data) return <Skeleton count={3} ratio="4:5" />;

  return (
    <div className="stack stack--lg">
      <section className="stats">
        <Tile
          label={t('admin.stat.accounts')}
          value={data.accounts.total}
          foot={t('admin.stat.new30', { n: data.accounts.new30 })}
        />
        <Tile
          label={t('admin.stat.people')}
          value={data.users.total}
          foot={t('admin.stat.active30', { n: data.users.active30 })}
        />
        <Tile label={t('admin.stat.framesPeriod')} value={data.work.framesThisPeriod} foot={data.period} />
        <Tile
          label={t('admin.stat.spend')}
          value={usd(data.spend.costMicros)}
          foot={t('admin.stat.spendFoot', { n: data.spend.frames })}
        />
      </section>

      <FramesChart daily={data.daily} t={t} />

      <section className="stats">
        <Tile
          label={t('admin.stat.rolls')}
          value={data.work.shoots}
          foot={t('admin.stat.models', { n: data.work.models })}
        />
        <Tile
          label={t('admin.stat.frames')}
          value={data.work.frames}
          foot={t('admin.stat.failed', { n: data.work.failed })}
        />
        <Tile
          label={t('admin.stat.storage')}
          value={bytes(data.work.storageBytes)}
          foot={t('admin.stat.storageFoot')}
        />
        <Tile
          label={t('admin.stat.perFrame')}
          value={usd(data.spend.perFrameMicros, 3)}
          foot={t('admin.stat.perFrameFoot')}
        />
      </section>

      <section className="panel stack stack--sm">
        <span className="label">{t('admin.health')}</span>
        <Row k={t('admin.stat.spendTop')} v={topStage(data.spend.byStage)} />
        <Row k={t('admin.health.suspended')} v={String(data.accounts.suspended)} />
        <Row k={t('admin.health.staff')} v={data.staff.join(', ') || '—'} />
        <p className="faint">{t('admin.health.staffHint')}</p>
      </section>
    </div>
  );
}

/**
 * Frames finished per day.
 *
 * One series, so no legend — the heading names it. Values are labelled only
 * where they say something (the busiest day and the last one); a number over
 * every bar is noise. Each bar carries its own tooltip, because a chart in a
 * browser that cannot be interrogated is a picture.
 */
function FramesChart({ daily, t }: { daily: AdminOverview['daily']; t: Translate }) {
  const max = Math.max(1, ...daily.map((d) => d.frames));
  const busiest = useMemo(
    () => daily.reduce((best, day) => (day.frames > best.frames ? day : best), daily[0]!),
    [daily],
  );
  const total = daily.reduce((sum, day) => sum + day.frames, 0);

  return (
    <section className="panel stack stack--sm">
      <div className="row row--between">
        <span className="label">{t('admin.chart.frames')}</span>
        <span className="mono faint">{t('admin.chart.total', { n: total })}</span>
      </div>

      {total === 0 && <p className="faint">{t('admin.chart.empty')}</p>}

      <div className="spark" role="img" aria-label={t('admin.chart.aria', { n: total, days: daily.length })}>
        {daily.map((day) => {
          const height = Math.max((day.frames / max) * 100, day.frames > 0 ? 4 : 1);
          const notable = day.frames > 0 && (day === busiest || day === daily[daily.length - 1]);
          return (
            <span
              key={day.date}
              className="spark__col"
              title={`${day.date} · ${day.frames}`}
              // The label sits just above its own bar, so it is never printed
              // over the fill it belongs to.
              style={{ ['--h' as string]: height }}
            >
              {notable && <span className="spark__value mono">{day.frames}</span>}
              <span
                className="spark__bar"
                style={{ height: `${height}%` }}
                data-empty={day.frames === 0 ? '' : undefined}
              />
              <span className="spark__tick mono">{day.date.slice(8)}</span>
            </span>
          );
        })}
      </div>
      {/*
       * The hiding goes on a wrapper, never on the table. A table box sizes
       * to its rows whatever height it is given, and `overflow` does not apply
       * to one — so `.sr-only` on the <table> itself left a fourteen-row table
       * four hundred pixels tall, absolutely positioned against the page
       * rather than the stage, and the whole window could be scrolled down
       * into empty ground to reach it. A block wrapper is one pixel square
       * and clips it, and the table keeps its role for a screen reader.
       */}
      <div className="sr-only">
        <table>
          <caption>{t('admin.chart.frames')}</caption>
          <tbody>
            {daily.map((day) => (
              <tr key={day.date}>
                <th scope="row">{day.date}</th>
                <td>{day.frames}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * One number, large. It used to be a `.tile` this page owned; it is now the
 * `.stat` every page uses, which is why the front desk finally looks like the
 * same product as the rest of the studio.
 */
function Tile({ label, value, foot }: { label: string; value: string | number; foot?: string }) {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
      {foot && <span className="stat__note">{foot}</span>}
    </div>
  );
}

const Row = ({ k, v }: { k: string; v: string }) => (
  <div className="row row--between">
    <span className="faint">{k}</span>
    <span className="mono">{v}</span>
  </div>
);

/* -------------------------------- accounts ------------------------------ */

function Accounts() {
  const t = useT();
  const date = useDateFormat();
  const toast = useToast();
  const describe = useErrorText();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | undefined>();

  // Debounced on the query, so typing does not fire a search per keystroke.
  const { data: page, reload: load } = useLoad(() => admin.accounts(query), {
    initial: { accounts: [] as AdminAccount[], total: 0 },
    deps: [query],
    debounce: 200,
    onError: (err) => toast(describe(err, 'admin.failed'), 'error'),
  });
  const rows = page.accounts;
  const total = page.total;

  return (
    <div className="stack">
      <input
        className="input"
        aria-label={t('admin.searchPlaceholder')}
        placeholder={t('admin.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <p className="faint">{t('admin.accountsFound', { n: total })}</p>

      <div className="table">
        {rows.map((account) => (
          <button
            key={account.id}
            type="button"
            className={`table__row ${open === account.id ? 'is-on' : ''}`}
            onClick={() => setOpen(open === account.id ? undefined : account.id)}
          >
            <span className="grow">
              <span className="table__title">{account.name}</span>
              <span className="faint mono" style={{ display: 'block' }}>
                {account.id} · {date.long(account.createdAt)}
              </span>
            </span>
            {account.status === 'suspended' && (
              <span className="pill pill--warn">{t('admin.suspended')}</span>
            )}
            <span className="mono faint">{t('admin.framesShort', { n: account.frames })}</span>
            <span className="mono faint">{usd(account.spendMicros)}</span>
          </button>
        ))}
      </div>

      {open && (
        <AccountPanel
          id={open}
          onChanged={() => load()}
          onGone={() => {
            setOpen(undefined);
            load();
          }}
        />
      )}
    </div>
  );
}

function AccountPanel({ id, onChanged, onGone }: { id: string; onChanged: () => void; onGone: () => void }) {
  const t = useT();
  const toast = useToast();
  const describe = useErrorText();
  const [confirm, setConfirm] = useState('');

  const { data: detail, reload: load } = useLoad(() => admin.account(id), {
    initial: undefined as AdminAccountDetail | undefined,
    deps: [id],
  });

  if (!detail) return <Skeleton count={1} ratio="4:5" />;

  const act = async (fn: () => Promise<unknown>, message: string) => {
    try {
      await fn();
      toast(message, 'ok');
      load();
      onChanged();
    } catch (err) {
      toast(describe(err, 'admin.failed'), 'error');
    }
  };

  return (
    <div className="stack">
      <section className="panel stack stack--sm">
        <span className="label">{detail.account.name}</span>
        <Row k={t('admin.detail.frames')} v={`${detail.work.frames}`} />
        <Row k={t('admin.detail.storage')} v={bytes(detail.work.storageBytes)} />
        <Row k={t('admin.detail.spend')} v={usd(detail.spend.costMicros)} />
        {detail.users.map((u) => (
          <div key={u.id} className="row row--between">
            <span>{u.name || u.email}</span>
            <span className="faint mono">{u.email}</span>
          </div>
        ))}
      </section>

      <section className="panel panel--danger stack stack--sm">
        <span className="label">{t('admin.danger')}</span>
        <button
          className="btn"
          onClick={() =>
            void act(
              () => admin.setStatus(id, detail.account.status === 'suspended' ? 'active' : 'suspended'),
              t('admin.done'),
            )
          }
        >
          {detail.account.status === 'suspended' ? t('admin.restore') : t('admin.suspend')}
        </button>
        <div className="field">
          <label htmlFor="c-confirm">{t('settings.typeName', { name: detail.account.name })}</label>
          <input
            id="c-confirm"
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <button
          className="btn btn--danger"
          disabled={confirm !== detail.account.name}
          onClick={async () => {
            try {
              await admin.removeAccount(id, confirm);
              toast(t('admin.done'), 'ok');
              onGone();
            } catch (err) {
              toast(describe(err, 'admin.failed'), 'error');
            }
          }}
        >
          <TrashIcon /> {t('admin.deleteAccount')}
        </button>
      </section>

      <details className="panel">
        <summary className="label" style={{ cursor: 'pointer' }}>
          {t('settings.activity')}
        </summary>
        <div className="log" style={{ marginTop: 14 }}>
          {detail.activity.map((entry, i) => (
            <div key={i} className="log__line">
              <span className="log__time">{new Date(entry.at).toLocaleString()}</span>
              <span>
                {entry.action}
                {entry.detail ? ` · ${entry.detail}` : ''}
              </span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

/* -------------------------------- helpers ------------------------------- */

/** The stage that ate most of the bill, with its share. */
function topStage(byStage: Record<string, number>): string {
  const entries = Object.entries(byStage).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((n, [, v]) => n + v, 0);
  const top = entries[0];
  if (!top || !total) return '—';
  return `${top[0]} ${Math.round((top[1] / total) * 100)}%`;
}

const bytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

/* ------------------------------ invitations ----------------------------- */

/**
 * The private beta's front door. An invitation is a link that opens one
 * account, once; sent to an address it opens it for that address only.
 */
function Invites() {
  const t = useT();
  const date = useDateFormat();
  const toast = useToast();
  const describe = useErrorText();
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  /** The link just made — shown once, because the server keeps only its digest. */
  const [made, setMade] = useState<{ url: string; emailed: boolean; email: string } | undefined>();

  const { data: invites, reload } = useLoad(() => admin.invites(), {
    initial: [] as AdminInvite[],
    onError: (err) => toast(describe(err, 'admin.failed'), 'error'),
  });

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await admin.createInvite({ email: email.trim(), note: note.trim() });
      setMade({ url: result.url, emailed: result.emailed, email: email.trim() });
      setEmail('');
      setNote('');
      reload();
    } catch (err) {
      toast(describe(err, 'admin.failed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast(t('admin.invite.copied'), 'ok');
    } catch {
      toast(t('admin.failed'), 'error');
    }
  };

  const revoke = async (id: string) => {
    try {
      await admin.revokeInvite(id);
      reload();
    } catch (err) {
      toast(describe(err, 'admin.failed'), 'error');
    }
  };

  return (
    <div className="house">
      {/* The invitation, on a block of ink: the one thing made on this tab. */}
      <form className="house__invite invert stack stack--sm" onSubmit={create}>
        <h2 className="house__inviteTitle">{t('admin.invite.new')}</h2>
        <p className="faint">{t('admin.invite.hint')}</p>
        <div className="field">
          <label htmlFor="invite-email">{t('admin.invite.email')}</label>
          <input
            id="invite-email"
            className="input"
            type="email"
            value={email}
            placeholder={t('admin.invite.emailPlaceholder')}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="invite-note">{t('admin.invite.note')}</label>
          <input
            id="invite-note"
            className="input"
            maxLength={200}
            value={note}
            placeholder={t('admin.invite.notePlaceholder')}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <button className="btn btn--primary" type="submit" disabled={busy}>
          {busy ? <span className="spinner" /> : null}
          {t('admin.invite.create')}
        </button>

        {made && (
          <div className="banner stack stack--sm">
            <span>
              {made.emailed ? t('admin.invite.emailed', { email: made.email }) : t('admin.invite.copyNow')}
            </span>
            <div className="row">
              <input
                className="input grow mono"
                readOnly
                value={made.url}
                aria-label={t('admin.invite.link')}
              />
              <button type="button" className="btn" onClick={() => void copy(made.url)}>
                {t('admin.invite.copy')}
              </button>
            </div>
          </div>
        )}
      </form>

      <div className="house__list">
        <p className="faint">{t('admin.invite.count', { n: invites.length })}</p>
        <div className="invites">
          {invites.map((invite) => (
            <div key={invite.id} className={`invite is-${invite.status}`}>
              <span className="invite__avatar" aria-hidden="true">
                {(invite.email || invite.note || '?').slice(0, 1).toUpperCase()}
              </span>
              <span className="invite__text">
                <span className="invite__title">{invite.email || invite.note || t('admin.invite.open')}</span>
                <span className="faint">
                  {invite.email && invite.note ? `${invite.note} · ` : ''}
                  {invite.status === 'used'
                    ? t('admin.invite.usedBy', {
                        email: invite.usedByEmail ?? '—',
                        date: date.short(invite.usedAt!),
                      })
                    : t('admin.invite.expires', { date: date.short(invite.expiresAt) })}
                </span>
              </span>
              <span className="invite__foot">
                <span className={`pill ${invite.status === 'pending' ? '' : 'pill--muted'}`}>
                  {t(`admin.invite.status.${invite.status}`)}
                </span>
                {invite.status === 'pending' && (
                  <button type="button" className="btn btn--sm" onClick={() => void revoke(invite.id)}>
                    {t('admin.invite.revoke')}
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
